import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

/**
 * Auth Context Provider
 * Manages user authentication state, login, register, and logout
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('rocketverse_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  /**
   * Register a new user
   */
  const register = (userData) => {
    const newUser = {
      id: Date.now().toString(),
      ...userData,
      role: 'customer',
      createdAt: new Date().toISOString()
    };
    setUser(newUser);
    localStorage.setItem('rocketverse_user', JSON.stringify(newUser));
    return { success: true, user: newUser };
  };

  /**
   * Login user
   */
  const login = (email, password) => {
    // Mock login - in real app, this would call an API
    const mockUser = {
      id: '1',
      email: email,
      name: 'John Doe',
      role: 'customer',
      createdAt: new Date().toISOString()
    };
    setUser(mockUser);
    localStorage.setItem('rocketverse_user', JSON.stringify(mockUser));
    return { success: true, user: mockUser };
  };

  /**
   * Logout user
   */
  const logout = () => {
    setUser(null);
    localStorage.removeItem('rocketverse_user');
  };

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = () => {
    return user !== null;
  };

  /**
   * Check if user has specific role
   */
  const hasRole = (role) => {
    return user?.role === role;
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    isAuthenticated,
    hasRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use Auth Context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
